/**
 * One rupee formatter for the whole storefront.
 *
 * Gift card prices carry paise, because the admin's markup is a percentage and
 * ₹250 at 1% is ₹252.50. Formatting those with `maximumFractionDigits: 0` — as
 * every call site used to — rounds to the nearest rupee in *either* direction,
 * so ₹252.40 advertises as ₹252 while checkout charges ₹252.40. Advertising
 * below the charged price is the failure worth designing against.
 *
 * So: whole rupees stay clean (₹1,299, not ₹1,299.00), and anything with a
 * fractional part shows it exactly. Ordinary products are always whole rupees
 * and are unaffected.
 */
export function formatINR(value) {
  const n = Number(value) || 0;
  // Rounded before the modulo: 503.99 * 100 is 50398.99999999999 in binary
  // floating point, and testing that directly reports paise on whole rupees.
  const hasPaise = Math.round(n * 100) % 100 !== 0;

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  }).format(n);
}

/**
 * The same, but a missing value reads as "—" instead of ₹0.
 *
 * `formatINR` coerces null and undefined to 0, which is right for a total that
 * really is nothing and wrong for a figure that has not arrived. A pricing panel
 * rendering "₹0" while its quote is still in flight — or from a field the API
 * stopped sending — states a price that is not true, and ₹0 is the one number a
 * gift card can never be.
 *
 * Use this anywhere the value comes from a response; use formatINR for sums the
 * page owns and knows are numbers.
 */
export function formatMoney(value) {
  return Number.isFinite(Number(value)) && value !== null && value !== ''
    ? formatINR(value)
    : '—';
}

/** Paise → rupees. The catalogue publishes every price in paise. */
export const paiseToRupees = (paise) => (Number(paise) || 0) / 100;
